import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticatedApi } from "../hooks/useAuthenticatedApi";
import { apiEndpoints } from "../utils/api";
import { navigateToLatestThread } from "../utils/threadNavigation";
import { Terminal, X, Check, ArrowRight, Server, Lock } from "lucide-react";
import useCreateWorkspace from "../hooks/useCreateWorkspace";
import { SANDBOX_WORKSPACE_PATH } from "./code-editor-panel/constants";
import { useTemplates } from "../hooks/useTemplates";
import type { TemplateWithAccess } from "../types/template";
import { useFeatures } from "../hooks/useFeatures";
import { useSubscriptionAccess } from "../hooks/useSubscriptionAccess";
import { getTemplateIcon } from "../utils/templateUtils";
import KolegaLoadingOverlay from "./KolegaLoadingOverlay";
import MixpanelService, { EventName } from "../mixpanel";
import { useIsLocalhost } from "../store/environmentStore";
import { useSettingsModal } from "../contexts/SettingsModalContext";

// Custom scrollbar styles (from ProjectSelectionPage)
const customScrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    transition: all 0.2s ease;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.25);
  }

  /* Dark mode */
  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
  }

  .dark .custom-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  }

  /* Smooth scrolling */
  .custom-scrollbar {
    scroll-behavior: smooth;
  }

  /* Hide scrollbar when not needed */
  .custom-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.08);
  }

  .dark .custom-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.08);
  }

  /* Ultra-thin scrollbar on hover for more Apple-like feel */
  .custom-scrollbar:hover::-webkit-scrollbar {
    width: 10px;
  }
`;

// Inject styles into the document head
const injectCustomScrollbarStyles = () => {
  const styleId = "new-project-modal-scrollbar-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = customScrollbarStyles;
    document.head.appendChild(style);
  }
};

// Inject styles
injectCustomScrollbarStyles();

const PROJECT_TYPES = [
  {
    id: "local",
    name: "Local Project",
    description:
      "Work with files on your local machine. Changes are saved directly to your computer.",
    icon: <Terminal />,
  },
  {
    id: "sandbox",
    name: "Cloud Sandbox",
    description:
      "Work in an isolated cloud environment. Changes are committed to a GitLab repository.",
    icon: <Server />,
  },
];

interface WorkspaceData {
  id: string;
  directory_path: string;
  name: string;
  port?: number;
  is_sandbox?: boolean;
}

interface CreateWorkspaceResult {
  data: WorkspaceData | null;
  loading: boolean;
  error: Error | null;
  createWorkspace: (
    path?: string,
    projectTemplateType?: string,
    isSandbox?: boolean,
    name?: string,
    extras?: any,
  ) => Promise<WorkspaceData | null>;
}

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const isLocalhost = useIsLocalhost();
  const authenticatedApi = useAuthenticatedApi();
  const { has, tier } = useFeatures();
  const { accessType } = useSubscriptionAccess();
  const githubEnabled = has("github-integration");
  const { openAccountSettings } = useSettingsModal();
  const [projectName, setProjectName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [projectType, setProjectType] = useState<"local" | "sandbox">("local");
  const [useCustomPath, setUseCustomPath] = useState(false);
  const [customPath, setCustomPath] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // GitHub-backed repo selection state (sandbox only)
  const [githubConnected, setGithubConnected] = useState<boolean>(false);
  const [loadingGithub, setLoadingGithub] = useState<boolean>(false);
  const [githubRepos, setGithubRepos] = useState<Array<{ full_name: string; private: boolean; default_branch: string }>>([]);
  const [selectedGithubRepo, setSelectedGithubRepo] = useState<string>("");
  const [selectedGithubBranch, setSelectedGithubBranch] = useState<string>("");
  // Removed: creating new GitHub repos is not supported
  const [importTemplateIntoExistingIfEmpty, setImportTemplateIntoExistingIfEmpty] = useState<boolean>(true);
  // Explicit opt-in to link sandbox to a GitHub repository instead of default GitLab
  const [useGithubRepo, setUseGithubRepo] = useState<boolean>(false);
  const { createWorkspace } = useCreateWorkspace(
    "",
    "",
    false,
  ) as CreateWorkspaceResult;
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
  } = useTemplates();

  // Convert project name to kebab-case for directory paths
  const toKebabCase = (str: string): string => {
    return str
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  // Get all templates (including inactive) and add empty project option (pro tier or trial)
  const isTrial = accessType !== 'subscription';
  const emptyProjectOption: TemplateWithAccess | null = (tier === "pro" || isTrial)
    ? {
        id: "no-template",
        name: "Empty Project",
        slug: "empty-project",
        description:
          "Start with a clean slate without any predefined files or structure.",
        category: "general",
        gitlab_url: "",
        git_branch: "main",
        icon: "code",
        tags: [],
        required_integrations: [],
        required_plans: ["pro"],
        is_active: true,
        is_public: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        order: 999,
        can_access: true,
        can_access_message: "",
      }
    : null;

  const allTemplates = emptyProjectOption ? [emptyProjectOption, ...templates] : [...templates];

  // Sort templates by order, then by name
  const availableTemplates = allTemplates.sort((a, b) => {
    // First sort by order (lower numbers first)
    const orderA = a.order ?? 999;
    const orderB = b.order ?? 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    // Then sort by name
    return a.name.localeCompare(b.name);
  });

  // Set default project type based on environment
  useEffect(() => {
    if (!isLocalhost) {
      setProjectType("sandbox");
    }
  }, [isLocalhost]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setProjectName("");
      setSelectedTemplate(null);
      setUseCustomPath(false);
      setCustomPath("");
      setErrorMessage("");
      setProjectType(isLocalhost ? "local" : "sandbox");
    }
  }, [isOpen, isLocalhost]);

  // Load GitHub status/repos when in sandbox mode, feature enabled, and user opted-in
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (projectType !== "sandbox" || !githubEnabled || !useGithubRepo) return;
      setLoadingGithub(true);
      try {
        const status = await authenticatedApi.get(apiEndpoints.githubStatus);
        if (cancelled) return;
        const connected = Boolean(status?.connected);
        setGithubConnected(connected);
        if (connected) {
          const repos = await authenticatedApi.get(apiEndpoints.githubRepos);
          if (cancelled) return;
          const normalized = (repos || []).map((r: any) => ({
            full_name: r.full_name,
            private: !!r.private,
            default_branch: r.default_branch || "main",
          }));
          setGithubRepos(normalized);
        } else {
          setGithubRepos([]);
        }
      } catch (e) {
        console.error("Failed to load GitHub status/repos", e);
      } finally {
        if (!cancelled) setLoadingGithub(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectType, authenticatedApi, githubEnabled, useGithubRepo]);

  const handleConnectGithub = async () => {
    try {
      const res = await authenticatedApi.get(apiEndpoints.githubConnect);
      if (res?.authorization_url) window.location.href = res.authorization_url;
    } catch (e) {
      console.error("Failed to initiate GitHub connection", e);
    }
  };

  const handleSelectGithubRepo = (fullName: string) => {
    setSelectedGithubRepo(fullName);
    const repo = githubRepos.find((r) => r.full_name === fullName);
    setSelectedGithubBranch(repo?.default_branch || "main");
  };

  // Select first backend template by default (prefer first active) for sandbox projects
  useEffect(() => {
    if (!isOpen) return;
    if (templatesLoading) return;
    if (projectType !== "sandbox") return;
    if (selectedTemplate) return;
    if (!templates || templates.length === 0) return;

    const sorted = [...templates].sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.name.localeCompare(b.name);
    });
    const firstActive = sorted.find(t => t.is_active && t.can_access) || sorted.find(t => t.is_active) || sorted[0];
    setSelectedTemplate(firstActive?.id ?? null);
  }, [isOpen, templatesLoading, templates, selectedTemplate, projectType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectName.trim() && !useCustomPath) {
      setErrorMessage("Please enter a project name");
      return;
    }

    if (useCustomPath && !customPath.trim()) {
      setErrorMessage("Please enter a project path");
      return;
    }

    // Template is optional; for existing GitHub repos we import only if checkbox is ON and repo is empty

    // GitHub validations (only if feature enabled AND user opted-in)
    if (projectType === "sandbox" && githubEnabled && useGithubRepo) {
      if (!githubConnected) {
        setErrorMessage("Please connect your GitHub account to continue");
        return;
      }
      if (!selectedGithubRepo) {
        setErrorMessage("Please select a GitHub repository");
        return;
      }
      if (importTemplateIntoExistingIfEmpty && !selectedTemplate) {
        setErrorMessage("Please select a template to import into the repository");
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMessage("");

    // Determine template id to send (optional for sandbox)
    const computedTemplateId = (() => {
      if (projectType === "local") return "no-template";
      // Existing GitHub repo: only send template if import-if-empty is ON
      if (selectedGithubRepo) return importTemplateIntoExistingIfEmpty ? (selectedTemplate || undefined) : undefined;
      return selectedTemplate || undefined;
    })();

    // Track project creation started
    MixpanelService.track(EventName.PROJECT_CREATION_STARTED, {
      projectName,
      templateId: computedTemplateId,
      projectType,
      useCustomPath,
      customPath: useCustomPath ? customPath : undefined,
    });

    try {
      // Determine directory path
      const directoryPath =
        projectType === "sandbox"
          ? SANDBOX_WORKSPACE_PATH
          : useCustomPath && customPath.trim()
            ? customPath.trim()
            : `~/KolegaProjects/${toKebabCase(projectName)}`;

      // For local projects always send 'no-template'; for sandbox send selected template id
      const templateId = computedTemplateId;

      // Build extras for GitHub-backed sandbox
    const extras =
        projectType === "sandbox" && githubEnabled && useGithubRepo && selectedGithubRepo
          ? {
              git_provider: "github",
              github_repo_full_name: selectedGithubRepo,
              github_branch: selectedGithubBranch || "main",
              import_template_into_existing_if_empty: importTemplateIntoExistingIfEmpty,
            }
          : {};

      const workspaceData = await createWorkspace(
        directoryPath,
        templateId,
        projectType === "sandbox",
        projectName,
        extras,
      );

      if (workspaceData) {
        // Track successful project creation
        MixpanelService.track(EventName.PROJECT_CREATION_COMPLETED, {
          projectName,
          templateId,
          projectType,
          workspaceId: workspaceData.id,
          useCustomPath,
          customPath: useCustomPath ? customPath : undefined,
        });

        onClose(); // Close modal before navigation
        await navigateToLatestThread(authenticatedApi, navigate, workspaceData.id);
      } else {
        throw new Error("Failed to create workspace");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create project. Please try again.";
      setErrorMessage(errorMessage);

      // Track project creation failure
      MixpanelService.track(EventName.PROJECT_CREATION_FAILED, {
        projectName,
        templateId: computedTemplateId,
        projectType,
        error: errorMessage,
        useCustomPath,
        customPath: useCustomPath ? customPath : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Check if selected template is accessible
  const selectedTemplateData = selectedTemplate 
    ? availableTemplates.find(t => t.id === selectedTemplate)
    : null;
  const isSelectedTemplateInaccessible = Boolean(
    projectType === "sandbox" 
    && selectedTemplateData 
    && selectedTemplateData.can_access === false
  );
  const disabledReason = isSelectedTemplateInaccessible && selectedTemplateData?.can_access_message
    ? selectedTemplateData.can_access_message
    : "";

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-slate-100 dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-slate-300 dark:border-neutral-800">
          {/* Modal Header */}
          <div className="px-8 pt-6 pb-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
            <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
              Create New Project
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <X size={20} className="text-neutral-500 dark:text-neutral-400" />
            </button>
          </div>

          {/* Scrollable Form Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] custom-scrollbar">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="px-8 py-6 space-y-6">
                {/* Error Message */}
                {errorMessage && (
                  <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 text-red-600 dark:text-red-400 text-sm">
                    {errorMessage}
                  </div>
                )}

                {/* Project Name */}
                <div>
                  <label
                    htmlFor="projectName"
                    className="block text-sm font-medium mb-2 text-neutral-700 dark:text-neutral-300"
                  >
                    Project Name
                  </label>
                  <input
                    type="text"
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 text-ui-text-primary dark:text-white rounded-xl border border-ui-border-primary dark:border-neutral-700 focus:ring-2 focus:ring-brand-primary dark:focus:ring-brand-primaryDarkMode focus:border-brand-primary dark:focus:border-brand-primaryDarkMode outline-none transition-all text-sm"
                    placeholder="My Awesome Project"
                    disabled={isSubmitting}
                    autoComplete="off"
                  />
                </div>

                {/* Project Type - only shown on localhost */}
                {isLocalhost && (
                  <div>
                    <h3 className="text-sm font-medium mb-3 text-neutral-700 dark:text-neutral-300">
                      Project Type
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {PROJECT_TYPES.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => {
                            setProjectType(type.id as "local" | "sandbox");
                            // Reset custom path when switching to sandbox
                            if (type.id === "sandbox") {
                              setUseCustomPath(false);
                            }
                          }}
                          className={`p-4 rounded-xl border text-left transition-all relative ${
                            projectType === type.id
                              ? "border-brand-action dark:border-brand-action bg-brand-action/10 dark:bg-brand-action/20"
                              : "border-ui-border-primary dark:border-neutral-700 hover:bg-slate-200/70 hover:shadow-sm dark:hover:bg-neutral-800/80"
                          }`}
                          disabled={isSubmitting}
                        >
                          {projectType === type.id && (
                            <div className="absolute top-3 right-3 text-brand-action">
                              <Check size={16} />
                            </div>
                          )}
                          <div className="flex">
                            <div
                              className={`mr-3 text-neutral-500 ${projectType === type.id ? "text-brand-action" : ""}`}
                            >
                              {type.icon}
                            </div>
                            <div>
                              <h4 className="font-medium text-sm text-neutral-900 dark:text-white">
                                {type.name}
                              </h4>
                              <p
                                className={`mt-1 text-xs text-neutral-500 dark:text-neutral-400 ${
                                  projectType === type.id
                                    ? "leading-relaxed"
                                    : "line-clamp-2"
                                }`}
                              >
                                {type.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Path Option - only for local projects on localhost */}
                {isLocalhost && projectType === "local" && (
                  <div>
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id="useCustomPath"
                        checked={useCustomPath}
                        onChange={(e) => setUseCustomPath(e.target.checked)}
                        className="w-4 h-4 text-brand-action bg-ui-background-primary border-ui-border-primary rounded focus:ring-brand-action/20 focus:ring-2"
                        disabled={isSubmitting}
                      />
                      <label
                        htmlFor="useCustomPath"
                        className="ml-2 text-sm font-medium text-neutral-700 dark:text-neutral-300"
                      >
                        Use custom project path
                      </label>
                    </div>

                    {useCustomPath && (
                      <input
                        type="text"
                        value={customPath}
                        onChange={(e) => setCustomPath(e.target.value)}
                        className="w-full px-4 py-3 bg-ui-background-primary dark:bg-neutral-800 text-ui-text-primary dark:text-white rounded-xl border border-ui-border-primary dark:border-neutral-700 focus:ring-2 focus:ring-brand-action/20 focus:border-brand-action outline-none transition-all text-sm"
                        placeholder="Enter full project path (e.g., /Users/username/Projects/my-project)"
                        disabled={isSubmitting}
                        autoComplete="off"
                      />
                    )}

                    {!useCustomPath && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Project will be created at: ~/KolegaProjects/
                        {projectName
                          ? toKebabCase(projectName)
                          : "project-name"}
                      </p>
                    )}
                  </div>
                )}

                {/* Sandbox: template selection (optional) */}
                {projectType === "sandbox" && (
                <div>
                  <h3 className="text-sm font-medium mb-3 text-neutral-700 dark:text-neutral-300">
                    Select a Template (optional)
                  </h3>
                  {templatesLoading && (
                    <div className="text-center py-4 text-neutral-500 dark:text-neutral-400">
                      Loading templates...
                    </div>
                  )}
                  {templatesError && (
                    <div className="px-4 py-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800/30 text-yellow-600 dark:text-yellow-400 text-sm mb-3">
                      Warning: Could not load templates. Using default options
                      only.
                    </div>
                  )}
                  <div className="max-h-64 overflow-y-auto space-y-1.5 custom-scrollbar">
                    {availableTemplates.map((template) => (
                      <div key={template.id} className="relative">
                        <div
                          onClick={() => {
                            if (template.is_active && !isSubmitting) {
                              setSelectedTemplate(template.id);
                              MixpanelService.track(
                                EventName.PROJECT_TEMPLATE_SELECTED,
                                {
                                  templateId: template.id,
                                  templateName: template.name,
                                  templateCategory: template.category,
                                },
                              );
                            }
                          }}
                          className={`w-full p-3 rounded-lg text-left transition-all flex items-center group cursor-pointer relative ${
                            selectedTemplate === template.id
                              ? "bg-brand-action/10 dark:bg-brand-action/20 border-2 border-brand-action dark:border-brand-action shadow-lg"
                              : template.is_active && template.can_access
                                ? "hover:bg-slate-100 hover:shadow-md dark:hover:bg-neutral-800 border-2 border-slate-200 dark:border-transparent bg-white dark:bg-neutral-800/50"
                                : "hover:bg-slate-100 dark:hover:bg-neutral-800 border-2 border-slate-200 dark:border-transparent bg-slate-50 dark:bg-neutral-800/30"
                          }`}
                        >
                          {selectedTemplate === template.id && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-action rounded-l-lg"></div>
                          )}
                          <div
                            className={`mr-2.5 flex-shrink-0 ${
                              selectedTemplate === template.id
                                ? "text-neutral-800 dark:text-brand-action"
                                : template.is_active && template.can_access
                                  ? "text-neutral-700 dark:text-neutral-500"
                                  : "text-neutral-600 dark:text-neutral-300"
                            }`}
                          >
                            {getTemplateIcon(template)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4
                                className={`font-medium text-sm flex items-center gap-2 ${
                                  selectedTemplate === template.id
                                    ? "text-neutral-900 dark:text-brand-action font-semibold"
                                    : template.is_active && template.can_access
                                      ? "text-neutral-900 dark:text-white"
                                      : "text-neutral-700 dark:text-neutral-200"
                                }`}
                              >
                                {template.name}
                                {!template.is_active && (
                                  <span className="ml-2 text-xs">
                                    (Unavailable)
                                  </span>
                                )}
                              </h4>
                              {selectedTemplate === template.id && (
                                <Check
                                  size={16}
                                  className="text-brand-action dark:text-brand-action flex-shrink-0"
                                />
                              )}
                            </div>
                            {!template.can_access && template.can_access_message && (
                              template.redirect_modal && template.redirect_section ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAccountSettings(template.redirect_section!);
                                  }}
                                  className="mt-1 inline-flex items-center gap-1 text-xs text-neutral-900 dark:text-brand-action font-semibold underline"
                                >
                                  <Lock size={11} className="text-neutral-700 dark:text-brand-action flex-shrink-0" />
                                  <span>
                                    {template.can_access_message}
                                  </span>
                                  <ArrowRight size={11} className="text-neutral-900 dark:text-brand-action" />
                                </button>
                              ) : (
                                <p className="mt-1 inline-flex items-center gap-1 text-xs text-neutral-900 dark:text-brand-action font-semibold">
                                  <Lock size={11} className="text-neutral-700 dark:text-brand-action flex-shrink-0" />
                                  {template.can_access_message}
                                </p>
                              )
                            )}
                            <p
                              className={`${!template.can_access && template.can_access_message ? 'mt-0.5' : 'mt-1'} text-xs ${
                                selectedTemplate === template.id
                                  ? "text-neutral-700 dark:text-neutral-400 leading-snug"
                                  : "text-neutral-600 dark:text-neutral-400 line-clamp-1"
                              }`}
                            >
                              {template.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Optional: Link to GitHub repo instead of default GitLab (explicit opt-in) */}
                  {githubEnabled && (
                    <div className="mt-6 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          id="use-github-repo"
                          type="checkbox"
                          checked={useGithubRepo}
                          onChange={(e) => {
                            const next = e.target.checked;
                            setUseGithubRepo(next);
                            if (!next) {
                              // Clear selections when toggled off
                              setSelectedGithubRepo("");
                              setSelectedGithubBranch("");
                            }
                          }}
                        />
                        <label htmlFor="use-github-repo" className="text-sm text-neutral-900 dark:text-white">
                          Link this project to a GitHub repository (instead of GitLab)
                        </label>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        When unchecked, sandbox projects use a standard GitLab repository by default.
                      </p>
                    </div>
                  )}

                  {/* GitHub section (shown only when feature enabled AND opted-in) */}
                  {githubEnabled && useGithubRepo && (
                    <div className="space-y-3 mt-6">
                      {!githubConnected ? (
                        <div className="space-y-2">
                          <div className="text-xs text-neutral-600 dark:text-neutral-400">
                            Connect your GitHub account to select a repository.
                          </div>
                          <button
                            type="button"
                            onClick={handleConnectGithub}
                            className="px-3 py-2 rounded-lg bg-brand-action text-brand-action-text text-sm"
                            disabled={loadingGithub}
                          >
                            {loadingGithub ? "Loading…" : "Connect GitHub"}
                          </button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="text-sm block mb-1" htmlFor="github-repo-select">Repository</label>
                            <select
                              id="github-repo-select"
                              value={selectedGithubRepo}
                              onChange={(e) => handleSelectGithubRepo(e.target.value)}
                              className="w-full px-3 py-2 rounded-lg bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 text-sm"
                            >
                              <option value="" disabled>
                                {loadingGithub ? "Loading repositories…" : "Select a repository"}
                              </option>
                              {githubRepos.map((r) => (
                                <option key={r.full_name} value={r.full_name}>
                                  {r.full_name}{r.private ? " (private)" : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm block mb-1" htmlFor="github-repo-branch">Branch</label>
                            <input
                              id="github-repo-branch"
                              value={selectedGithubBranch}
                              onChange={(e) => setSelectedGithubBranch(e.target.value)}
                              placeholder="main"
                              className="w-full px-3 py-2 rounded-lg bg-ui-background-primary dark:bg-neutral-800 border border-ui-border-primary dark:border-neutral-700 text-sm"
                            />
                          </div>
                          {/* Safe-guarded import option (moved below branch) */}
                          <div className="flex items-center gap-2">
                            <input
                              id="gh-import-if-empty"
                              type="checkbox"
                              checked={importTemplateIntoExistingIfEmpty}
                              onChange={(e) => setImportTemplateIntoExistingIfEmpty(e.target.checked)}
                            />
                            <label htmlFor="gh-import-if-empty" className="text-sm">
                              Import selected template into existing repo if empty
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            </form>
          </div>

          {/* Modal Footer - Fixed at bottom */}
          <div className="px-8 py-6 border-t border-neutral-100 dark:border-neutral-800 flex justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-ui-background-primary dark:bg-neutral-800 hover:bg-slate-200 hover:shadow-sm dark:hover:bg-neutral-700 text-ui-text-primary dark:text-neutral-300 transition-all text-sm"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {isSelectedTemplateInaccessible && selectedTemplateData ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 text-sm">
                  <Lock size={16} className="text-neutral-500 dark:text-neutral-400" />
                  <span className="font-medium">{disabledReason || "Feature locked"}</span>
                </div>
                {selectedTemplateData.redirect_modal && selectedTemplateData.redirect_section && (
                  <button
                    onClick={() => openAccountSettings(selectedTemplateData.redirect_section!)}
                    className="px-4 py-2 bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-xl flex items-center font-medium transition-all shadow-sm text-sm"
                    type="button"
                  >
                    Unlock now
                    <ArrowRight size={16} className="ml-1.5" />
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                className={`px-5 py-2 bg-brand-action hover:bg-brand-action-hover text-brand-action-text rounded-xl flex items-center font-medium transition-all shadow-sm text-sm ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={isSubmitting}
                type="button"
              >
                {isSubmitting ? "Creating..." : "Create Project"}
                {!isSubmitting && <ArrowRight size={16} className="ml-1.5" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isSubmitting && (
        <KolegaLoadingOverlay
          isVisible={isSubmitting}
          message={"Creating project..."}
        />
      )}
    </>
  );
};

export default NewProjectModal;
